import * as React from 'react';
import { Label } from 'components/ui/label';
import { cn } from 'lib/utils';
import { toUzNational, toUzE164, UZ_DIAL_CODE } from 'lib/phone';

export interface AuthPhoneFieldProps {
  label: string;
  /** Stored value in E.164 (e.g. "+998880727174") or "" when empty. */
  value: string;
  /** Receives the composed E.164 value (or "" when cleared). */
  onChange: (e164: string) => void;
  onBlur?: () => void;
  error?: string;
  /** Marks the field wrong without a message of its own, e.g. after bad credentials. */
  invalid?: boolean;
  id?: string;
  name?: string;
  /** National-part placeholder shown after the fixed +998 segment. */
  placeholder?: string;
  autoFocus?: boolean;
  disabled?: boolean;
}

/**
 * A grey "+998" segment, then the national number; pasting a full number
 * strips the redundant country code. The value stays E.164.
 */
export const AuthPhoneField = React.forwardRef<HTMLInputElement, AuthPhoneFieldProps>(
  (
    { label, value, onChange, onBlur, error, invalid, id, name, placeholder = '90 123 45 67', autoFocus, disabled },
    ref,
  ) => {
    const inputId = id ?? name ?? 'phone';
    const national = toUzNational(value ?? '');
    const isInvalid = Boolean(error || invalid);

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      onChange(toUzE164(event.target.value));
    };

    return (
      <div className="space-y-1.5">
        <Label htmlFor={inputId} className="text-[13.5px] font-medium text-foreground/80">
          {label}
        </Label>
        <div
          className={cn(
            'flex h-11 items-stretch overflow-hidden rounded-[10px] border border-border bg-card transition-colors hover:border-foreground/20',
            // The input clips inside the segment, so the focus outline sits on the whole field.
            'focus-within:outline focus-within:outline-2 focus-within:outline-offset-2 focus-within:outline-[hsl(var(--ring))]',
            isInvalid && 'border-destructive hover:border-destructive',
            disabled && 'opacity-60',
          )}
        >
          <span className="flex select-none items-center border-r border-border bg-background px-3 text-base tabular-nums text-foreground/80 md:text-[15px]">
            +{UZ_DIAL_CODE}
          </span>
          <input
            ref={ref}
            id={inputId}
            name={name}
            type="tel"
            inputMode="numeric"
            autoComplete="off"
            aria-invalid={isInvalid ? true : undefined}
            disabled={disabled}
            // eslint-disable-next-line jsx-a11y/no-autofocus -- callers opt in when the field is the form's first step
            autoFocus={autoFocus}
            value={national}
            onChange={handleChange}
            onBlur={onBlur}
            placeholder={placeholder}
            className="h-full min-w-0 flex-1 bg-transparent px-3 text-base tabular-nums outline-none placeholder:text-muted-foreground/70 focus-visible:!outline-none disabled:cursor-not-allowed md:text-[15px]"
          />
        </div>
        {error ? <p className="text-xs font-medium text-error-700 dark:text-error-400">{error}</p> : null}
      </div>
    );
  },
);

AuthPhoneField.displayName = 'AuthPhoneField';
